import { Suspense } from 'react';
import { ErrorBanner, Spinner } from '@platform/ui';
import { resolveView } from '../registry/viewRegistry';
import { GridFitNote } from '../views/grid/GridFitNote';
import { customTabViewsOf } from './filters/tabViews';
import { StageTabs } from '../views/StageTabs';
import type { IRecordListContentProps } from './IRecordListContentProps';
import { ListCount } from './ListCount';
import { ListEmpty } from './ListEmpty';
import { Pagination } from './Pagination';
import { SelectionBar } from './toolbar/SelectionBar';
import { useCapability } from './toolbar/useCapability';
import { countPlacement, emptyModeOf, isPagedView, notesHiddenColumns } from './viewKinds';

/**
 * Тіло сторінки таблиці (прокручується під шапкою): липка смуга дій з вибраним, помилка завантаження, примітка про сховані стовпці над
 * картками, дошкою, календарем і власним поданням-вкладкою (бібліотека, як у прототипі; таблиця показує її сама), подання з реєстру, пагінація для
 * таблиці, карток і редагування в рядку або рядок «N записів» для подань усього списку (над дошкою й календарем, під етапами). Порожній
 * список у порядку прототипу (noRows · вкладки етапів · hasColsHidden): таблиця й картки — рамка з реченням і дією, під нею примітка про
 * сховані стовпці; етапи — рамка, вкладки й примітка; решта — саме подання (порожні колонки, сітка календаря). Подання, що вантажать дані
 * самі (аналітика, панель), — без смуги вибраного, порожнього стану й пагінації; розділений вигляд займає все тіло сторінки — без смуги вибраного.
 */
export function RecordListContent({ model, actions, canExport, unfiltered, empty }: IRecordListContentProps) {
  const View = resolveView(model.state.view);
  const page = unfiltered ? undefined : model.page;
  const view = model.state.view;
  const isEmpty = page !== undefined && page.items.length === 0 && !model.isLoading;
  const mode = emptyModeOf(view);
  const boxed = isEmpty && mode !== 'view';
  const count = page ? countPlacement(view) : null;
  const total = page?.total ?? 0;
  const canEdit = useCapability(model.ws, 'edit');
  const canDelete = useCapability(model.ws, 'delete');
  return (
    <>
      {!unfiltered && view !== 'split' && <SelectionBar count={model.selection.length} actions={actions} canExport={canExport} canEdit={canEdit} canDelete={canDelete} />}
      {model.error && <ErrorBanner error={model.error} />}
      {!boxed && ((!unfiltered && notesHiddenColumns(view)) || customTabViewsOf(model.rt).includes(view)) && <GridFitNote model={model} />}
      {count === 'above' && <ListCount total={total} placement="above" />}
      <Suspense fallback={<Spinner />}>
        {boxed ? (
          <>
            <ListEmpty {...empty} />
            {mode === 'tabs' && <StageTabs model={model} />}
            <GridFitNote model={model} />
          </>
        ) : <View model={model} />}
      </Suspense>
      {count === 'below' && <ListCount total={total} placement="below" />}
      {page && isPagedView(view) && (
        <Pagination page={page.page} pageSize={model.state.pageSize} total={page.total} onPage={(p) => model.setState({ page: p })}
          onPageSize={(s) => model.setState({ pageSize: s })} />
      )}
    </>
  );
}
